import React, { useState, useEffect } from 'react';
import { X, ArrowDownCircle, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Item, Location, User as UserType } from '@/types';
import { apiService } from '@/services/api';
import toast from 'react-hot-toast';

interface DirectCheckoutModalProps {
  item: Item | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const DirectCheckoutModal: React.FC<DirectCheckoutModalProps> = ({
  item,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    quantity: 1,
    targetUserId: '',
    notes: '',
    sapPlantNumber: '',
  });
  const [users, setUsers] = useState<UserType[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      fetchLocations();
      if (item) {
        setFormData({
          quantity: 1,
          targetUserId: '',
          notes: `${item.isRental ? 'Ausleihe' : 'Entnahme'} - ${item.name}`,
          sapPlantNumber: '',
        });
      }
    }
  }, [isOpen, item]);

  const fetchUsers = async () => {
    try {
      const response = await apiService.get<UserType[]>('/users');
      setUsers(response || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await apiService.get<Location[]>('/locations');
      setLocations(response || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!item) return;
    
    if (formData.quantity <= 0 || formData.quantity > item.quantity) {
      toast.error('Ungültige Menge eingegeben');
      return;
    }

    // Validierung für SAP-geführte Items
    if (item.isManageInSAP && !formData.sapPlantNumber) {
      toast.error('Bitte wählen Sie eine Werknummer für dieses SAP-geführte Item aus.');
      return;
    }

    setLoading(true);
    try {
      // Erstelle eine Transaction für den Warenausgang
      const transactionData: any = {
        itemId: item.id,
        type: 'CHECKOUT',
        quantity: formData.quantity,
        notes: formData.notes || `Admin ${item.isRental ? 'Ausleihe' : 'Entnahme'}`,
      };

      // Füge Benutzer hinzu, falls ausgewählt
      if (formData.targetUserId) {
        transactionData.userId = formData.targetUserId;
      }

      // Füge SAP-Werknummer hinzu, falls das Item SAP-geführt ist
      if (item.isManageInSAP && formData.sapPlantNumber) {
        transactionData.sapPlantNumber = formData.sapPlantNumber;
      }


      await apiService.post('/transactions', transactionData);

      toast.success(`${item.isRental ? 'Ausleihe' : 'Entnahme'} erfolgreich verbucht`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error processing checkout:', error);
      toast.error('Fehler beim Warenausgang');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (!isOpen || !item) return null;

  const actionText = item.isRental ? 'Ausleihe' : 'Entnahme';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="flex items-center gap-2">
            <ArrowDownCircle className="h-5 w-5 text-red-600" />
            Warenausgang - {actionText}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {/* Item Info */}
          <div className="bg-muted p-4 rounded-lg mb-6">
            <h3 className="font-semibold text-lg mb-2">{item.name}</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Mat. Nr:</span> {item.sku || '-'}
              </div>
              <div>
                <span className="text-muted-foreground">Aktueller Bestand:</span> {item.quantity}
              </div>
              <div>
                <span className="text-muted-foreground">Standort:</span> {item.location?.name || '-'}
              </div>
              <div>
                <span className="text-muted-foreground">Art:</span>
                <Badge variant={item.isRental ? "default" : "secondary"} className="ml-2">
                  {item.isRental ? 'Mietgerät' : 'Verbrauchsmaterial'}
                </Badge>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Menge für {actionText} *</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                max={item.quantity}
                value={formData.quantity}
                onChange={(e) => handleInputChange('quantity', parseInt(e.target.value) || 1)}
                required
              />
              <p className="text-xs text-muted-foreground">
                Verfügbar: {item.quantity} Stück
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="targetUserId">Für Benutzer (optional)</Label>
              <select
                id="targetUserId"
                value={formData.targetUserId}
                onChange={(e) => handleInputChange('targetUserId', e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
              >
                <option value="">Benutzer auswählen...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
            </div>

            {/* SAP Werknummer Auswahl - nur für SAP-geführte Items */}
            {item.isManageInSAP && (
              <div className="space-y-2">
                <Label htmlFor="sapPlantNumber" className="flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  SAP Werknummer (erforderlich) *
                </Label>
                <select
                  id="sapPlantNumber"
                  value={formData.sapPlantNumber}
                  onChange={(e) => {
                    console.log('SAP Plant Number changed to:', e.target.value);
                    handleInputChange('sapPlantNumber', e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                  required
                >
                  <option value="">Bitte Werknummer auswählen...</option>
                  {locations
                    .filter(location => location.plantNumber)
                    .map(location => (
                      <option key={location.id} value={location.plantNumber}>
                        Werk {location.plantNumber} - {location.name}
                      </option>
                    ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Für SAP-geführte Items ist die Angabe einer Werknummer erforderlich.
                </p>
                {item.isManageInSAP && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <p className="text-sm text-blue-800">
                      <strong>SAP-geführtes Item:</strong> Diese Entnahme wird zur späteren Übertragung in SAP vorgemerkt.
                    </p>
                  </div>
                )}
              </div>
            )}


            <div className="space-y-2">
              <Label htmlFor="notes">Kommentar (optional)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder={`Zusätzliche Informationen zur ${actionText}...`}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Zusätzliche Informationen zur Entnahme (z.B. Verwendungszweck, Projekt)
              </p>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Abbrechen
              </Button>
              <Button 
                type="submit" 
                disabled={loading || formData.quantity <= 0 || formData.quantity > item.quantity}
                className="bg-red-600 hover:bg-red-700"
              >
                {loading ? `${actionText} wird verbucht...` : `${actionText} verbuchen`}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default DirectCheckoutModal;
